import { useRef, useState } from "react";
import { Flex, Text, Heading, Card, Switch, Button, Callout } from "@radix-ui/themes";
import { useAIAssistantSettings } from "hooks/aiAssistant/useAIAssistantSettings";
import { useUpdateAIAssistantSettings } from "hooks/aiAssistant/useUpdateAIAssistantSettings";
import { AssistantStatus, AssistantLoadError } from "./AssistantStatus";

export default function AIAssistantSettings() {
  const query = useAIAssistantSettings();
  const update = useUpdateAIAssistantSettings();
  const inFlight = useRef(false);
  const [requestError, setRequestError] = useState("");

  const synchronize = async (enabled) => {
    if (inFlight.current || update.isPending) return;
    inFlight.current = true;
    setRequestError("");
    try {
      await update.mutateAsync(enabled);
    } catch (error) {
      setRequestError(error.response?.data?.detail || "B-Connect could not confirm synchronization. Reload settings before retrying.");
    } finally {
      inFlight.current = false;
    }
  };

  if (query.isPending) return <Text role="status">Loading AI Assistant settings…</Text>;
  if (query.isError) return <AssistantLoadError error={query.error} onRetry={query.refetch} />;
  const settings = query.data;
  const unresolved = settings.sync_status === "pending" || settings.sync_status === "error";
  return (
    <Flex direction="column" gap="5" width="100%" maxWidth="680px">
      <Flex direction="column" gap="2">
        <Heading size="6">AI Assistant settings</Heading>
        <Text color="gray">One central AI Assistant for your organisation, shared across your brands and locations.</Text>
      </Flex>
      <Card>
        <Flex direction="column" gap="5">
          <Flex align="center" justify="between" gap="4">
            <Flex direction="column" gap="1">
              <Text as="label" htmlFor="ai-assistant-enabled" weight="medium">Enable AI Assistant</Text>
              <Text size="2" color="gray">Enabling provisions your organisation’s central Assistant.</Text>
            </Flex>
            <Switch id="ai-assistant-enabled" aria-label="Enable AI Assistant" checked={settings.enabled}
              disabled={update.isPending || settings.sync_status === "pending"} onCheckedChange={synchronize} />
          </Flex>
          <AssistantStatus settings={settings} />
          {settings.sync_status === "unconfigured" && <Text size="2" color="gray">No runtime Assistant has been provisioned yet.</Text>}
          {settings.sync_status === "synced" && <Text size="2">{settings.enabled ? "Your Assistant is enabled and synchronized." : "Your Assistant is disabled and synchronized. Its public ID is retained for re-enablement."}</Text>}
          {(update.isPending || settings.sync_status === "pending") && <Text role="status">Synchronization is pending. The runtime state is not yet confirmed.</Text>}
          {!settings.enabled && settings.sync_status !== "synced" && settings.sync_status !== "unconfigured" && (
            <Text size="2">B-Connect access is disabled. Public runtime channels are only confirmed disabled after synchronization succeeds.</Text>
          )}
          {(settings.last_sync_error || requestError) && <Callout.Root color="red" role="alert"><Callout.Text>{requestError || settings.last_sync_error}</Callout.Text></Callout.Root>}
          {unresolved && <Flex gap="3">
            <Button disabled={update.isPending} onClick={() => synchronize(settings.enabled)}>Retry synchronization</Button>
            <Button variant="soft" color="gray" disabled={update.isPending} onClick={() => query.refetch()}>Reload settings</Button>
          </Flex>}
          {requestError && !unresolved && <Button variant="soft" onClick={() => query.refetch()}>Reload settings</Button>}
        </Flex>
      </Card>
    </Flex>
  );
}
